import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { workflowI18n } from './i18n';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import { getPermissionGroupsTree } from '../../services/roleServices';
import { fetchAllSystemUsers } from '../../services/TaskService';
import {
  NODE_TYPE,
  getWorkflowNomenclatures,
} from '../../services/workflowService';
import { getRoles } from '../../services/usersServices';
import 'reactflow/dist/style.css';
import './workflowEditorModal.scss';

const START_NODE_ID = 'wf_start';
const END_NODE_ID = 'wf_end';

const APPROVER_TYPE = {
  USER: 1,
  ROLE: 2,
  GROUP: 3,
};

const EDITOR_NODE_KIND = {
  START: 'start',
  END: 'end',
  TASK: 'task',
  APPROVAL: 'approval',
};


const parseEnumNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'task') return NODE_TYPE.TASK;
  if (normalized === 'approval') return NODE_TYPE.APPROVAL;
  if (normalized === 'user') return APPROVER_TYPE.USER;
  if (normalized === 'role') return APPROVER_TYPE.ROLE;
  if (normalized === 'group') return APPROVER_TYPE.GROUP;

  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const normalizeToken = (value) => {
  const raw = String(value ?? '').trim().toLowerCase();
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const sanitizeCode = (value, fallback) => {
  const raw = String(value ?? '').trim().toUpperCase();
  const cleaned = raw.replace(/[^A-Z0-9/_-]+/g, '_').replace(/^_+|_+$/g, '');
  if (cleaned) return cleaned.slice(0, 60);
  return fallback.slice(0, 60);
};

const normalizeNumberArray = (values) => {
  if (!Array.isArray(values)) return [];

  const seen = new Set();
  const normalized = [];
  values.forEach((value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0 || seen.has(parsed)) return;
    seen.add(parsed);
    normalized.push(parsed);
  });

  return normalized;
};

const parseApproverUserIdsFromMetadata = (metadataJson) => {
  if (!metadataJson || typeof metadataJson !== 'string') return [];

  try {
    const parsed = JSON.parse(metadataJson);
    if (!parsed || typeof parsed !== 'object') return [];

    const candidates = [
      parsed.approverUserIds,
      parsed.approvers,
      parsed.selectedApproverUserIds,
      parsed.approvalUserIds,
      parsed.userIds,
    ];

    for (const candidate of candidates) {
      if (!Array.isArray(candidate)) continue;

      const ids = normalizeNumberArray(
        candidate.map((value) => {
          if (value && typeof value === 'object') {
            return value.id ?? value.value ?? value.userId ?? null;
          }

          return value;
        })
      );

      if (ids.length > 0) {
        return ids;
      }
    }
  } catch (_error) {
    return [];
  }

  return [];
};

const uniqueByValue = (options) => {
  const seen = new Set();
  const result = [];

  options.forEach((option) => {
    const value = Number(option?.value);
    if (!Number.isFinite(value) || seen.has(value)) return;
    seen.add(value);
    result.push({ label: String(option?.label || value), value });
  });

  return result;
};

const createEdgeId = (source, target) => `${source}->${target}`;

const buildEdge = (source, target) => ({
  id: createEdgeId(source, target),
  source,
  target,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 18,
    height: 18,
    color: '#1f7cc4',
  },
  style: { stroke: '#1f7cc4', strokeWidth: 2 },
});

const isSameOptionValue = (left, right) => {
  if (left === right) return true;
  if (left === null || left === undefined || right === null || right === undefined) {
    return false;
  }

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  const bothNumbers = Number.isFinite(leftNumber) && Number.isFinite(rightNumber);
  if (bothNumbers) return leftNumber === rightNumber;

  return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
};

const findOption = (options, value) =>
  options.find((item) => isSameOptionValue(item.value, value)) || null;

const isWorkflowNode = (node) =>
  node?.id !== START_NODE_ID && node?.id !== END_NODE_ID;

const getEmptyGraph = (t) => ({
  nodes: [
    {
      id: START_NODE_ID,
      type: 'workflowNode',
      position: { x: 80, y: 145 },
      draggable: true,
      selectable: true,
      data: {
        kind: EDITOR_NODE_KIND.START,
        title: t('editor_node_start'),
      },
    },
    {
      id: END_NODE_ID,
      type: 'workflowNode',
      position: { x: 760, y: 145 },
      draggable: true,
      selectable: true,
      data: {
        kind: EDITOR_NODE_KIND.END,
        title: t('editor_node_end'),
      },
    },
  ],
  edges: [buildEdge(START_NODE_ID, END_NODE_ID)],
});

const resolveApproverHint = (nodeData, userOptions, roleOptions, groupOptions, t) => {
  const type = Number(nodeData?.approverType);
  if (type === APPROVER_TYPE.USER) {
    const selectedUsers = normalizeNumberArray(nodeData?.approverUserIds);
    const fallbackUserId = Number(nodeData?.approverUserId);
    const userIds =
      selectedUsers.length > 0
        ? selectedUsers
        : Number.isFinite(fallbackUserId) && fallbackUserId > 0
        ? [fallbackUserId]
        : [];

    if (userIds.length === 0) return t('editor_node_hint_user_fallback');

    const labels = userIds.map(
      (userId) =>
        findOption(userOptions, userId)?.label ||
        t('wf_approver_user_fallback', { id: userId })
    );

    if (labels.length === 1) return labels[0];
    if (labels.length === 2)
      return t('editor_node_hint_user_and', { a: labels[0], b: labels[1] });
    return t('editor_node_hint_user_more', {
      a: labels[0],
      b: labels[1],
      rest: labels.length - 2,
    });
  }
  if (type === APPROVER_TYPE.ROLE) {
    const role = findOption(roleOptions, nodeData?.approverRoleId);
    return role
      ? t('editor_node_hint_role', { name: role.label })
      : t('editor_node_hint_role_fallback');
  }
  if (type === APPROVER_TYPE.GROUP) {
    const group = findOption(groupOptions, nodeData?.approverGroupId);
    return group
      ? t('editor_node_hint_group', { name: group.label })
      : t('editor_node_hint_group_fallback');
  }
  return t('editor_node_hint_undefined');
};

const WorkflowFlowNode = ({ data }) => {
  const kind = data?.kind || EDITOR_NODE_KIND.TASK;
  const cssKind =
    kind === EDITOR_NODE_KIND.START
      ? 'start'
      : kind === EDITOR_NODE_KIND.END
      ? 'end'
      : kind === EDITOR_NODE_KIND.APPROVAL
      ? 'approval'
      : 'task';

  return (
    <div className={`workflow-flow-node workflow-flow-node--${cssKind}`}>
      {kind !== EDITOR_NODE_KIND.START && (
        <Handle type='target' position={Position.Left} />
      )}

      <div className='workflow-flow-node__content'>
        <p className='workflow-flow-node__title'>{data.title}</p>
        {data.subtitle && <p className='workflow-flow-node__subtitle'>{data.subtitle}</p>}
        {data.hint && <p className='workflow-flow-node__hint'>{data.hint}</p>}
      </div>

      {kind !== EDITOR_NODE_KIND.END && (
        <Handle type='source' position={Position.Right} />
      )}
    </div>
  );
};

const nodeTypes = {
  workflowNode: WorkflowFlowNode,
};

const SelectField = ({
  Dropdown,
  label,
  placeholder = 'Selecciona...',
  options,
  value,
  onChange,
  disabled = false,
  forceNative = false,
}) => {
  if (Dropdown && !forceNative) {
    return (
      <Dropdown
        label={label}
        placeholder={placeholder}
        options={options}
        value={findOption(options, value)}
        onChange={(selected) => onChange(selected?.value ?? '')}
        width='100%'
        disabled={disabled}
      />
    );
  }

  return (
    <div>
      <label className='form-label'>{label}</label>
      <select
        className='form-select'
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value=''>{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const buildNodeData = ({ index, nodeType = NODE_TYPE.TASK }, t) => {
  const isApproval = Number(nodeType) === NODE_TYPE.APPROVAL;
  const defaultCode = isApproval ? `APPROVAL_${index}` : '';
  const defaultName = isApproval
    ? t('editor_default_name_approval', { index })
    : t('editor_default_name_task', { index });

  return {
    key: `node_${index}`,
    code: defaultCode,
    name: defaultName,
    nodeType: isApproval ? NODE_TYPE.APPROVAL : NODE_TYPE.TASK,
    sequenceOrder: index,
    isRequired: true,
    approverType: null,
    approverUserId: null,
    approverUserIds: [],
    approverRoleId: null,
    approverGroupId: null,
  };
};

const ensureUniqueNodeCodes = (workflowNodes, t) => {
  const seen = new Set();

  for (const node of workflowNodes) {
    const code = sanitizeCode(node.data.code, `NODE_${node.id}`);
    if (seen.has(code.toLowerCase())) {
      return t('editor_error_duplicate_code', { code });
    }
    seen.add(code.toLowerCase());
  }

  return null;
};

const ensureAcyclicWorkflow = (workflowNodes, workflowEdges) => {
  const nodeIds = new Set(workflowNodes.map((node) => node.id));
  const indegree = {};
  const adjacency = {};

  workflowNodes.forEach((node) => {
    indegree[node.id] = 0;
    adjacency[node.id] = [];
  });

  workflowEdges.forEach((edge) => {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) return;
    adjacency[edge.source].push(edge.target);
    indegree[edge.target] += 1;
  });

  const queue = Object.keys(indegree).filter((nodeId) => indegree[nodeId] === 0);
  let visited = 0;

  while (queue.length > 0) {
    const current = queue.shift();
    visited += 1;

    adjacency[current].forEach((nextNodeId) => {
      indegree[nextNodeId] -= 1;
      if (indegree[nextNodeId] === 0) queue.push(nextNodeId);
    });
  }

  return visited === workflowNodes.length;
};

const computeNodeDepths = (workflowNodes, allEdges) => {
  const nodeIds = new Set(workflowNodes.map((node) => node.id));
  const depth = {};
  const indegree = {};
  const adjacency = {};
  const starts = new Set();

  workflowNodes.forEach((node) => {
    depth[node.id] = 1;
    indegree[node.id] = 0;
    adjacency[node.id] = [];
  });

  allEdges.forEach((edge) => {
    if (edge.source === START_NODE_ID && nodeIds.has(edge.target)) {
      starts.add(edge.target);
      return;
    }

    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      adjacency[edge.source].push(edge.target);
      indegree[edge.target] += 1;
    }
  });

  const queue = Object.keys(indegree).filter((nodeId) => indegree[nodeId] === 0);
  const topo = [];

  while (queue.length > 0) {
    const current = queue.shift();
    topo.push(current);
    adjacency[current].forEach((nextNodeId) => {
      indegree[nextNodeId] -= 1;
      if (indegree[nextNodeId] === 0) queue.push(nextNodeId);
    });
  }

  topo.forEach((nodeId) => {
    if (starts.has(nodeId)) {
      depth[nodeId] = Math.max(depth[nodeId], 1);
    }

    adjacency[nodeId].forEach((nextNodeId) => {
      depth[nextNodeId] = Math.max(depth[nextNodeId], depth[nodeId] + 1);
    });
  });

  return depth;
};

const WorkflowEditorModal = ({
  DesignSystemComponents,
  show,
  mode = 'create',
  initialDefinition = null,
  existingDefinitions = [],
  currentUserId,
  onClose,
  onSaved,
}) => {
  const { Modal, Input, Dropdown, IconButton, ComboBox } = DesignSystemComponents;

  const { language } = useLanguage();
  const wfLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: workflowI18n });

  useEffect(() => {
    workflowI18n.changeLanguage(wfLanguage);
  }, [wfLanguage]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeCounter, setNodeCounter] = useState(1);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [flowInstance, setFlowInstance] = useState(null);
  const [flowRenderKey, setFlowRenderKey] = useState(0);

  const [userOptions, setUserOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [nomenclatureOptions, setNomenclatureOptions] = useState([]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  const approverTypeOptions = useMemo(
    () => [
      { label: t('editor_approver_type_user'), value: APPROVER_TYPE.USER },
      { label: t('editor_approver_type_role'), value: APPROVER_TYPE.ROLE },
      { label: t('editor_approver_type_group'), value: APPROVER_TYPE.GROUP },
    ],
    [t]
  );

  const nodeTypeOptions = useMemo(
    () => [
      { label: t('editor_node_type_task'), value: NODE_TYPE.TASK },
      { label: t('editor_node_type_approval'), value: NODE_TYPE.APPROVAL },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { label: t('editor_status_active'), value: true },
      { label: t('editor_status_inactive'), value: false },
    ],
    [t]
  );

  const workflowNodes = useMemo(
    () => nodes.filter((node) => isWorkflowNode(node)),
    [nodes]
  );

  const workflowNodeIds = useMemo(
    () => new Set(workflowNodes.map((node) => node.id)),
    [workflowNodes]
  );

  const normalizedUsedNomenclatures = useMemo(() => {
    const used = new Set();
    existingDefinitions.forEach((definition) => {
      if (
        mode === 'edit' &&
        initialDefinition?.id &&
        definition?.id === initialDefinition.id
      ) {
        return;
      }

      const nomenclature = String(definition?.nomenclature || '').trim();
      if (!nomenclature) return;
      used.add(nomenclature.toLowerCase());
    });
    return used;
  }, [existingDefinitions, initialDefinition?.id, mode]);

  const linkOptions = useMemo(() => {
    const base = workflowNodes.map((node) => ({
      label: node.data.name || node.data.code || node.id,
      value: node.id,
    }));
    base.push({ label: t('editor_node_finalization'), value: END_NODE_ID });
    return base;
  }, [workflowNodes]);

  const workflowLabelById = useMemo(() => {
    const map = {};
    workflowNodes.forEach((node) => {
      map[node.id] = node.data.name || node.data.code || node.id;
    });
    return map;
  }, [workflowNodes]);

  useEffect(() => {
    if (!show) return;

    let ignore = false;
    const loadOptions = async () => {
      const [usersResult, rolesResult, groupsResult, nomenclatureResult] =
        await Promise.allSettled([
          fetchAllSystemUsers(),
          getRoles(),
          getPermissionGroupsTree.getTree(),
          getWorkflowNomenclatures(),
        ]);

      if (ignore) return;

      if (usersResult.status === 'fulfilled') {
        const users = Array.isArray(usersResult.value) ? usersResult.value : [];
        const mapped = users.map((user) => ({
          label: user.label || user.name || `Usuario #${user.backendId || user.id}`,
          value: Number(user.backendId || user.id || user.value),
        }));
        setUserOptions(uniqueByValue(mapped));
      } else {
        setUserOptions([]);
      }

      if (rolesResult.status === 'fulfilled') {
        const raw = rolesResult.value;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        const mapped = list.map((role) => ({
          label: role.name || role.nombre || `Rol #${role.id}`,
          value: Number(role.id || role.value),
        }));
        setRoleOptions(uniqueByValue(mapped));
      } else {
        setRoleOptions([]);
      }

      if (groupsResult.status === 'fulfilled') {
        const groups = Array.isArray(groupsResult.value) ? groupsResult.value : [];
        const mapped = groups.map((group) => ({
          label: group.name || group.label || `Grupo #${group.id}`,
          value: Number(group.id || group.value),
        }));
        setGroupOptions(uniqueByValue(mapped));
      } else {
        setGroupOptions([]);
      }

      if (nomenclatureResult.status === 'fulfilled') {
        const seen = new Set();
        const unique = (Array.isArray(nomenclatureResult.value)
          ? nomenclatureResult.value
          : []
        ).filter((item) => {
          const value = String(item?.value || '').trim();
          if (!value) return false;
          const key = value.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        const cleaned = unique.map((item) => {
          const value = String(item.value || '').trim();
          return {
            label: String(item.label || value).trim() || value,
            value,
          };
        });

        setNomenclatureOptions(cleaned);
      } else {
        setNomenclatureOptions([]);
      }
    };

    loadOptions();
    return () => {
      ignore = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setFlowRenderKey((value) => value + 1);
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const emptyGraph = getEmptyGraph(t);

    if (!initialDefinition || mode === 'create') {
      setForm({
        name: '',
        description: '',
        isActive: true,
      });
      setNodes(emptyGraph.nodes);
      setEdges(emptyGraph.edges);
      setNodeCounter(1);
      setSelectedNodeId(null);
      setError('');
      return;
    }

    const definitionNodes = Array.isArray(initialDefinition.nodes)
      ? initialDefinition.nodes
      : [];
    const definitionTransitions = Array.isArray(initialDefinition.transitions)
      ? initialDefinition.transitions
      : [];
    const sequenceGroups = {};

    definitionNodes.forEach((node) => {
      const order = Number(node.sequenceOrder) || 1;
      if (!sequenceGroups[order]) sequenceGroups[order] = [];
      sequenceGroups[order].push(node);
    });

    const orders = Object.keys(sequenceGroups)
      .map(Number)
      .sort((a, b) => a - b);
    const visualNodeByWorkflowId = new Map();
    const visualWorkflowNodes = [];
    let maxCounter = definitionNodes.length + 1;

    orders.forEach((order, orderIndex) => {
      const group = sequenceGroups[order];
      const total = group.length;

      group.forEach((node, index) => {
        const visualId = `wf_node_${node.id || `${order}_${index}`}`;
        visualNodeByWorkflowId.set(node.id, visualId);

        const parsedType = parseEnumNumber(node.nodeType);
        const nodeType =
          parsedType === NODE_TYPE.APPROVAL ? NODE_TYPE.APPROVAL : NODE_TYPE.TASK;
        const isApproval = nodeType === NODE_TYPE.APPROVAL;
        const metadataApproverIds = parseApproverUserIdsFromMetadata(
          node.metadataJson
        );
        const fallbackApproverId = Number(node.approverUserId);
        const approverUserIds =
          metadataApproverIds.length > 0
            ? metadataApproverIds
            : Number.isFinite(fallbackApproverId) && fallbackApproverId > 0
            ? [fallbackApproverId]
            : [];

        const yOffset = (index - (total - 1) / 2) * 130;
        visualWorkflowNodes.push({
          id: visualId,
          type: 'workflowNode',
          position: { x: 260 + orderIndex * 220, y: 145 + yOffset },
          data: {
            key: normalizeToken(node.code || node.name || `node_${node.id}`),
            code: node.code || `NODE_${node.id}`,
            name: node.name || t('editor_node_name_fallback', { id: node.id }),
            title: node.name || t('editor_node_name_fallback', { id: node.id }),
            subtitle: isApproval
              ? t('editor_node_subtitle_approval')
              : t('editor_node_subtitle_task'),
            hint: isApproval
              ? `${t('editor_node_hint_user_fallback')} ${node.approverUserName || node.approverRoleName || node.approverGroupName || ''}`.trim()
              : null,
            kind: isApproval ? EDITOR_NODE_KIND.APPROVAL : EDITOR_NODE_KIND.TASK,
            nodeType,
            sequenceOrder: Number(node.sequenceOrder) || order,
            isRequired: node.isRequired !== false,
            approverType: parseEnumNumber(node.approverType),
            approverUserId: node.approverUserId || null,
            approverUserIds,
            approverRoleId: node.approverRoleId || null,
            approverGroupId: node.approverGroupId || null,
            metadataJson: node.metadataJson || null,
          },
        });
      });

      maxCounter += 1;
    });

    const visualEdges = [];
    const outgoing = new Set();

    definitionTransitions.forEach((transition) => {
      const targetId = visualNodeByWorkflowId.get(transition.toWorkflowNodeId);
      if (!targetId) return;

      const sourceId = transition.fromWorkflowNodeId
        ? visualNodeByWorkflowId.get(transition.fromWorkflowNodeId)
        : START_NODE_ID;

      if (!sourceId) return;
      visualEdges.push(buildEdge(sourceId, targetId));
      if (sourceId !== START_NODE_ID) {
        outgoing.add(sourceId);
      }
    });

    visualWorkflowNodes.forEach((node) => {
      if (!outgoing.has(node.id)) {
        visualEdges.push(buildEdge(node.id, END_NODE_ID));
      }
    });

    if (visualWorkflowNodes.length === 0) {
      visualEdges.push(buildEdge(START_NODE_ID, END_NODE_ID));
    }

    const uniqueEdgeIds = new Set();
    const uniqueEdges = visualEdges.filter((edge) => {
      if (uniqueEdgeIds.has(edge.id)) return false;
      uniqueEdgeIds.add(edge.id);
      return true;
    });

    setForm({
      name: initialDefinition.name || '',
      description: initialDefinition.description || '',
      isActive: initialDefinition.isActive !== false,
    });
    setNodes([...emptyGraph.nodes, ...visualWorkflowNodes]);
    setEdges(uniqueEdges);
    setNodeCounter(maxCounter);
    setSelectedNodeId(visualWorkflowNodes[0]?.id || null);
    setError('');
  }, [show, mode, initialDefinition, setEdges, setNodes]);

  useEffect(() => {
    if (!show || !flowInstance) return;

    const timer = setTimeout(() => {
      flowInstance.fitView({ padding: 0.2, duration: 250 });
    }, 80);

    return () => clearTimeout(timer);
  }, [show, nodes.length, edges.length, flowInstance]);

  const updateNode = (nodeId, patch) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.id !== nodeId) return node;

        const nextData = { ...node.data, ...patch };
        const isApproval = Number(nextData.nodeType) === NODE_TYPE.APPROVAL;
        const nextKind = isApproval
          ? EDITOR_NODE_KIND.APPROVAL
          : EDITOR_NODE_KIND.TASK;

        return {
          ...node,
          data: {
            ...nextData,
            title: nextData.name || node.data.name || t('editor_node_title_fallback'),
            subtitle: isApproval
              ? t('editor_node_subtitle_approval')
              : t('editor_node_subtitle_task'),
            hint: isApproval
              ? resolveApproverHint(nextData, userOptions, roleOptions, groupOptions, t)
              : null,
            kind: nextKind,
            approverType: isApproval ? nextData.approverType : null,
            approverUserId: isApproval ? nextData.approverUserId : null,
            approverUserIds: isApproval
              ? normalizeNumberArray(nextData.approverUserIds)
              : [],
            approverRoleId: isApproval ? nextData.approverRoleId : null,
            approverGroupId: isApproval ? nextData.approverGroupId : null,
          },
        };
      })
    );
  };

  const addLink = (source, target) => {
    if (!source || !target) return;
    if (source === END_NODE_ID || target === START_NODE_ID || source === target) {
      return;
    }

    setEdges((prevEdges) => {
      if (prevEdges.some((edge) => edge.source === source && edge.target === target)) {
        return prevEdges;
      }

      const withoutDirect =
        source !== START_NODE_ID || target !== END_NODE_ID
          ? prevEdges.filter(
              (edge) =>
                !(edge.source === START_NODE_ID && edge.target === END_NODE_ID)
            )
          : prevEdges;

      return addEdge(buildEdge(source, target), withoutDirect);
    });
  };

  const removeLink = (source, target) => {
    if (!source || !target) return;
    if (source === START_NODE_ID && target === END_NODE_ID && workflowNodes.length > 0) {
      return;
    }

    setEdges((prevEdges) =>
      prevEdges.filter((edge) => !(edge.source === source && edge.target === target))
    );
  };

  const addWorkflowNode = (nodeType) => {
    const nextIndex = nodeCounter;
    const nodeId = `wf_node_new_${nextIndex}`;
    const baseData = buildNodeData({ index: nextIndex, nodeType }, t);
    const isApproval = Number(baseData.nodeType) === NODE_TYPE.APPROVAL;

    const newNode = {
      id: nodeId,
      type: 'workflowNode',
      position: { x: 260 + workflowNodes.length * 180, y: 145 },
      data: {
        ...baseData,
        title: baseData.name,
        subtitle: isApproval
          ? t('editor_node_subtitle_approval')
          : t('editor_node_subtitle_task'),
        kind: isApproval ? EDITOR_NODE_KIND.APPROVAL : EDITOR_NODE_KIND.TASK,
        hint: null,
      },
    };

    setNodes((prevNodes) => [...prevNodes, newNode]);
    setNodeCounter((value) => value + 1);
    setSelectedNodeId(nodeId);

    const sourceCandidate =
      selectedNodeId && selectedNodeId !== END_NODE_ID
        ? selectedNodeId
        : workflowNodes.at(-1)?.id || START_NODE_ID;

    setEdges((prevEdges) => {
      let nextEdges = prevEdges.filter(
        (edge) => !(edge.source === START_NODE_ID && edge.target === END_NODE_ID)
      );

      // Al insertar un nodo en línea, reemplazamos el cierre directo del origen
      // para evitar conexiones automáticas sobrantes hacia Finalización.
      nextEdges = nextEdges.filter(
        (edge) => !(edge.source === sourceCandidate && edge.target === END_NODE_ID)
      );

      if (!nextEdges.some((edge) => edge.source === sourceCandidate && edge.target === nodeId)) {
        nextEdges = addEdge(buildEdge(sourceCandidate, nodeId), nextEdges);
      }

      if (!nextEdges.some((edge) => edge.source === nodeId && edge.target === END_NODE_ID)) {
        nextEdges = addEdge(buildEdge(nodeId, END_NODE_ID), nextEdges);
      }

      return nextEdges;
    });
  };

  const removeWorkflowNode = (nodeId) => {
    if (!workflowNodeIds.has(nodeId)) return;

    setEdges((prevEdges) => {
      const incoming = prevEdges.filter((edge) => edge.target === nodeId);
      const outgoing = prevEdges.filter((edge) => edge.source === nodeId);
      const kept = prevEdges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      );

      const bridged = [...kept];

      incoming.forEach((inEdge) => {
        outgoing.forEach((outEdge) => {
          if (inEdge.source === outEdge.target) return;
          if (inEdge.source === END_NODE_ID || outEdge.target === START_NODE_ID) {
            return;
          }
          const id = createEdgeId(inEdge.source, outEdge.target);
          if (!bridged.some((edge) => edge.id === id)) {
            bridged.push(buildEdge(inEdge.source, outEdge.target));
          }
        });
      });

      const stillHasWorkflow = workflowNodes.length > 1;
      if (!stillHasWorkflow && !bridged.some((edge) => edge.target === END_NODE_ID)) {
        bridged.push(buildEdge(START_NODE_ID, END_NODE_ID));
      }

      return bridged;
    });

    setNodes((prevNodes) => prevNodes.filter((node) => node.id !== nodeId));
    setSelectedNodeId(null);
  };

  const onConnect = (connection) => {
    addLink(connection.source, connection.target);
  };

  const onEdgeClick = (event, edge) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    removeLink(edge?.source, edge?.target);
  };

  const getApproverTargetOptions = (approverType) => {
    const type = Number(approverType);
    if (type === APPROVER_TYPE.USER) return userOptions;
    if (type === APPROVER_TYPE.ROLE) return roleOptions;
    if (type === APPROVER_TYPE.GROUP) return groupOptions;
    return [];
  };

  const getTaskNomenclatureOptions = (currentCode) => {
    const options = Array.isArray(nomenclatureOptions) ? [...nomenclatureOptions] : [];
    const normalizedCurrent = String(currentCode || '').trim();
    if (!normalizedCurrent) return options;

    const exists = options.some((option) =>
      isSameOptionValue(option?.value, normalizedCurrent)
    );
    if (exists) return options;

    return [{ label: normalizedCurrent, value: normalizedCurrent }, ...options];
  };

  const getWorkflowNomenclatureFromNodes = () => {
    const firstTaskNode = workflowNodes.find(
      (node) => Number(node.data.nodeType) === NODE_TYPE.TASK
    );
    if (!firstTaskNode) return '';

    const rawCode = String(firstTaskNode.data.code || '').trim();
    if (!rawCode) return '';

    return (
      nomenclatureOptions.find((option) => isSameOptionValue(option?.value, rawCode))
        ?.value ?? rawCode
    );
  };

  const validateGraph = () => {
    if (!form.name.trim()) return t('editor_error_no_name');

    const normalizedNomenclature = getWorkflowNomenclatureFromNodes();
    if (!normalizedNomenclature) {
      return t('editor_error_no_nomenclature');
    }
    const nomenclatureExists = nomenclatureOptions.some((option) =>
      isSameOptionValue(option?.value, normalizedNomenclature)
    );
    if (!nomenclatureExists && mode !== 'edit') {
      return t('editor_error_invalid_nomenclature');
    }
    if (normalizedUsedNomenclatures.has(normalizedNomenclature.toLowerCase())) {
      return t('editor_error_duplicate_nomenclature', { code: normalizedNomenclature });
    }

    if (workflowNodes.length === 0) {
      return t('editor_error_no_nodes');
    }

    const tasksCount = workflowNodes.filter(
      (node) => Number(node.data.nodeType) === NODE_TYPE.TASK
    ).length;
    const approvalsCount = workflowNodes.filter(
      (node) => Number(node.data.nodeType) === NODE_TYPE.APPROVAL
    ).length;

    if (tasksCount === 0 || approvalsCount === 0) {
      return t('editor_error_missing_task_approval');
    }

    const duplicatedCodeError = ensureUniqueNodeCodes(workflowNodes, t);
    if (duplicatedCodeError) return duplicatedCodeError;

    for (const node of workflowNodes) {
      const code = sanitizeCode(node.data.code, '');
      const name = String(node.data.name || '').trim();
      if (!code || !name) {
        return t('editor_error_empty_node');
      }

      if (Number(node.data.nodeType) === NODE_TYPE.APPROVAL) {
        const approverType = Number(node.data.approverType);
        if (!approverType) {
          return t('editor_error_no_approver_type', { name });
        }

        if (
          approverType === APPROVER_TYPE.USER &&
          normalizeNumberArray(node.data.approverUserIds).length === 0 &&
          !Number(node.data.approverUserId)
        ) {
          return t('editor_error_no_approver_user', { name });
        }

        if (
          approverType === APPROVER_TYPE.ROLE &&
          !Number(node.data.approverRoleId)
        ) {
          return t('editor_error_no_approver_role', { name });
        }

        if (
          approverType === APPROVER_TYPE.GROUP &&
          !Number(node.data.approverGroupId)
        ) {
          return t('editor_error_no_approver_group', { name });
        }
      }
    }

    const relevantEdges = edges.filter(
      (edge) =>
        (edge.source === START_NODE_ID || workflowNodeIds.has(edge.source)) &&
        (edge.target === END_NODE_ID || workflowNodeIds.has(edge.target))
    );

    const startEdges = relevantEdges.filter(
      (edge) => edge.source === START_NODE_ID && workflowNodeIds.has(edge.target)
    );
    if (startEdges.length === 0) {
      return t('editor_error_no_start');
    }

    const endEdges = relevantEdges.filter(
      (edge) => edge.target === END_NODE_ID && workflowNodeIds.has(edge.source)
    );
    if (endEdges.length === 0) {
      return t('editor_error_no_end');
    }

    const workflowEdges = relevantEdges.filter(
      (edge) => workflowNodeIds.has(edge.source) && workflowNodeIds.has(edge.target)
    );

    if (!ensureAcyclicWorkflow(workflowNodes, workflowEdges)) {
      return t('editor_error_cycles');
    }

    const reachable = new Set();
    const queue = [START_NODE_ID];
    const adjacency = {};

    relevantEdges.forEach((edge) => {
      if (!adjacency[edge.source]) adjacency[edge.source] = [];
      adjacency[edge.source].push(edge.target);
    });

    while (queue.length > 0) {
      const current = queue.shift();
      (adjacency[current] || []).forEach((next) => {
        if (reachable.has(next)) return;
        reachable.add(next);
        queue.push(next);
      });
    }

    const unreachable = workflowNodes.find((node) => !reachable.has(node.id));
    if (unreachable) {
      return t('editor_error_unreachable', { name: unreachable.data.name });
    }

    const reverse = {};
    relevantEdges.forEach((edge) => {
      if (!reverse[edge.target]) reverse[edge.target] = [];
      reverse[edge.target].push(edge.source);
    });

    const reachesEnd = new Set();
    const reverseQueue = [END_NODE_ID];
    while (reverseQueue.length > 0) {
      const current = reverseQueue.shift();
      (reverse[current] || []).forEach((prev) => {
        if (reachesEnd.has(prev)) return;
        reachesEnd.add(prev);
        reverseQueue.push(prev);
      });
    }

    const deadEnd = workflowNodes.find((node) => !reachesEnd.has(node.id));
    if (deadEnd) {
      return t('editor_error_dead_end', { name: deadEnd.data.name });
    }

    return null;
  };

  const buildPayload = () => {
    const canonicalNomenclature = getWorkflowNomenclatureFromNodes();

    const sortedNodes = [...workflowNodes].sort((a, b) => {
      if (a.position.x !== b.position.x) return a.position.x - b.position.x;
      return a.position.y - b.position.y;
    });

    const usedKeys = new Set();
    const nodeKeyById = {};
    const makeUniqueKey = (candidate, fallback) => {
      const normalizedCandidate =
        normalizeToken(candidate) || normalizeToken(fallback) || fallback;
      let key = normalizedCandidate;
      let suffix = 2;

      while (usedKeys.has(key) || !key) {
        key = `${normalizedCandidate}_${suffix}`;
        suffix += 1;
      }

      usedKeys.add(key);
      return key;
    };

    sortedNodes.forEach((node, index) => {
      const fallback = `node_${index + 1}`;
      const candidate = node.data.key || node.data.code || fallback;
      const key = makeUniqueKey(candidate, fallback);
      nodeKeyById[node.id] = key;
    });

    const edgeScope = edges.filter(
      (edge) =>
        (edge.source === START_NODE_ID || workflowNodeIds.has(edge.source)) &&
        (edge.target === END_NODE_ID || workflowNodeIds.has(edge.target))
    );

    const workflowEdges = edgeScope.filter((edge) => edge.target !== END_NODE_ID);
    const depths = computeNodeDepths(sortedNodes, edgeScope);

    const nodeEntriesById = {};
    const nodesPayload = sortedNodes.map((node, index) => {
      const nodeType = Number(node.data.nodeType) || NODE_TYPE.TASK;
      const approverType = Number(node.data.approverType) || null;
      const baseKey = nodeKeyById[node.id];
      const baseCode = sanitizeCode(node.data.code, `NODE_${index + 1}`);
      const baseName = String(node.data.name || '').trim();
      const baseSequenceOrder = depths[node.id] || index + 1;
      const isRequired = node.data.isRequired !== false;
      const selectedUserIds = normalizeNumberArray(node.data.approverUserIds);
      const fallbackUserId = Number(node.data.approverUserId);
      const approverUserIds =
        selectedUserIds.length > 0
          ? selectedUserIds
          : Number.isFinite(fallbackUserId) && fallbackUserId > 0
          ? [fallbackUserId]
          : [];
      const metadataJson =
        nodeType === NODE_TYPE.APPROVAL &&
        approverType === APPROVER_TYPE.USER &&
        approverUserIds.length > 0
          ? JSON.stringify({ approverUserIds })
          : null;

      const singleNode = {
        key: baseKey,
        code: baseCode,
        name: baseName,
        nodeType,
        sequenceOrder: baseSequenceOrder,
        isRequired,
        approverType: nodeType === NODE_TYPE.APPROVAL ? approverType : null,
        approverUserId:
          nodeType === NODE_TYPE.APPROVAL && approverType === APPROVER_TYPE.USER
            ? approverUserIds.length === 1
              ? approverUserIds[0]
              : null
            : null,
        approverRoleId:
          nodeType === NODE_TYPE.APPROVAL && approverType === APPROVER_TYPE.ROLE
            ? Number(node.data.approverRoleId) || null
            : null,
        approverGroupId:
          nodeType === NODE_TYPE.APPROVAL && approverType === APPROVER_TYPE.GROUP
            ? Number(node.data.approverGroupId) || null
            : null,
        metadataJson,
      };

      nodeEntriesById[node.id] = {
        entryKey: singleNode.key,
        exitKey: singleNode.key,
        chainedNodes: [singleNode],
      };

      return singleNode;
    });

    const groupedBySource = {};
    workflowEdges.forEach((edge) => {
      const sourceKey = edge.source === START_NODE_ID ? '__start__' : edge.source;
      if (!groupedBySource[sourceKey]) groupedBySource[sourceKey] = [];
      groupedBySource[sourceKey].push(edge);
    });

    const rawTransitions = [];
    Object.keys(groupedBySource).forEach((sourceKey) => {
      const scoped = groupedBySource[sourceKey].sort((a, b) => {
        const targetA = sortedNodes.find((node) => node.id === a.target);
        const targetB = sortedNodes.find((node) => node.id === b.target);
        if (!targetA || !targetB) return 0;
        if (targetA.position.x !== targetB.position.x) {
          return targetA.position.x - targetB.position.x;
        }
        return targetA.position.y - targetB.position.y;
      });

      scoped.forEach((edge, index) => {
        const sourceMapping = nodeEntriesById[edge.source];
        const targetMapping = nodeEntriesById[edge.target];

        rawTransitions.push({
          fromNodeKey:
            edge.source === START_NODE_ID
              ? null
              : sourceMapping?.exitKey || nodeKeyById[edge.source],
          toNodeKey: targetMapping?.entryKey || nodeKeyById[edge.target],
          transitionOrder: index + 1,
          isRequired: true,
        });
      });
    });

    const transitionsBySource = rawTransitions.reduce((acc, transition) => {
      const sourceKey = transition.fromNodeKey || '__start__';
      if (!acc[sourceKey]) acc[sourceKey] = [];
      acc[sourceKey].push(transition);
      return acc;
    }, {});

    const transitionsPayload = Object.values(transitionsBySource).flatMap((items) =>
      items.map((transition, index) => ({
        ...transition,
        transitionOrder: index + 1,
      }))
    );

    return {
      name: form.name.trim(),
      nomenclature: String(canonicalNomenclature || '').trim(),
      description: form.description?.trim() || null,
      isActive: Boolean(form.isActive),
      nodes: nodesPayload,
      transitions: transitionsPayload,
    };
  };

  const handleSave = async () => {
    if (saving) return;
    const validationError = validateGraph();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = buildPayload();
      await onSaved?.({
        mode,
        definitionId: initialDefinition?.id || null,
        payload,
        actorUserId: currentUserId || null,
      });
    } catch (saveError) {
      setError(
        saveError?.customMessage ||
          saveError?.message ||
          t('editor_error_save')
      );
    } finally {
      setSaving(false);
    }
  };

  if (!Modal || !Input) return null;

  return (
    <Modal
      title={mode === 'edit' ? t('editor_modal_title_edit') : t('editor_modal_title_create')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={mode === 'edit' ? t('editor_btn_save') : t('editor_btn_create')}
      primaryBtnAction={handleSave}
      secondaryBtnTxt={t('editor_btn_cancel')}
      secondaryBtnAction={onClose}
      disabledPrimaryBtn={saving}
      width={960}
    >
      <div className='workflow-editor-modal'>
        {error && <p className='color-error-500 mb-3'>{error}</p>}

        <div className='row g-3 mb-3'>
          <div className='col-12 col-md-6'>
            <Input
              label={t('editor_field_name')}
              placeholder={t('editor_field_name_placeholder')}
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </div>
          <div className='col-12 col-md-6'>
            <SelectField
              Dropdown={Dropdown}
              label={t('editor_field_status')}
              options={statusOptions}
              value={form.isActive}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, isActive: String(value) === 'true' }))
              }
            />
          </div>
        </div>

        <div className='workflow-editor-modal__head'>
          <h4 className='fw-semibold mb-0'>{t('editor_section_flow')}</h4>
          <div className='d-flex align-items-center gap-2'>
            <button
              type='button'
              className='workflow-editor-modal__add-btn'
              onClick={() => addWorkflowNode(NODE_TYPE.TASK)}
            >
              {t('editor_btn_add_task')}
            </button>
            <button
              type='button'
              className='workflow-editor-modal__add-btn'
              onClick={() => addWorkflowNode(NODE_TYPE.APPROVAL)}
            >
              {t('editor_btn_add_approval')}
            </button>
          </div>
        </div>

        <div className='workflow-editor-flow'>
          <ReactFlowProvider>
            <ReactFlow
              key={flowRenderKey}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onEdgeClick={onEdgeClick}
              nodeTypes={nodeTypes}
              nodesDraggable
              nodesConnectable
              elementsSelectable
              panOnDrag
              fitView
              fitViewOptions={{ padding: 0.2 }}
              onInit={(instance) => {
                setFlowInstance(instance);
                setTimeout(() => {
                  instance.fitView({ padding: 0.2, duration: 250 });
                }, 0);
              }}
              onSelectionChange={(selection) => {
                const selected = selection?.nodes?.[0];
                setSelectedNodeId(selected?.id || null);
              }}
            >
              <Background color='#d4dcea' gap={24} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        <div className='workflow-editor-help'>
          <h5 className='fw-semibold mb-2'>{t('editor_help_title')}</h5>
          <ul>
            <li>{t('editor_help_1')}</li>
            <li>{t('editor_help_2')}</li>
            <li>{t('editor_help_3')}</li>
            <li>{t('editor_help_4')}</li>
          </ul>
        </div>

        <div className='workflow-editor-steps'>
          {workflowNodes.map((node) => {
            const isApproval = Number(node.data.nodeType) === NODE_TYPE.APPROVAL;
            const approverType = Number(node.data.approverType) || '';
            const approverOptions = getApproverTargetOptions(approverType);
            const outgoingLinks = edges.filter(
              (edge) =>
                edge.source === node.id &&
                (workflowNodeIds.has(edge.target) || edge.target === END_NODE_ID)
            );
            const usedTargets = new Set(outgoingLinks.map((edge) => edge.target));
            const connectOptions = linkOptions.filter(
              (option) => option.value !== node.id && !usedTargets.has(option.value)
            );
            const selectedApproverValue =
              approverType === APPROVER_TYPE.USER
                ? node.data.approverUserId
                : approverType === APPROVER_TYPE.ROLE
                ? node.data.approverRoleId
                : approverType === APPROVER_TYPE.GROUP
                ? node.data.approverGroupId
                : '';
            const selectedApproverUserIds =
              approverType === APPROVER_TYPE.USER
                ? normalizeNumberArray(node.data.approverUserIds)
                : [];
            const selectedApproverUserOptions = selectedApproverUserIds
              .map((userId) => findOption(approverOptions, userId))
              .filter(Boolean);

            return (
              <div
                key={node.id}
                className={`workflow-editor-step ${
                  selectedNodeId === node.id ? 'workflow-editor-step--selected' : ''
                }`}
              >
                <div className='workflow-editor-step__fields'>
                  <Input
                    label={t('editor_node_field_name')}
                    value={node.data.name || ''}
                    onChange={(event) =>
                      updateNode(node.id, { name: event.target.value })
                    }
                  />
                  <Input
                    label={t('editor_node_field_code')}
                    value={node.data.code || ''}
                    onChange={(event) =>
                      updateNode(node.id, { code: event.target.value })
                    }
                  />
                  <SelectField
                    Dropdown={Dropdown}
                    label={t('editor_node_field_type')}
                    options={nodeTypeOptions}
                    value={node.data.nodeType}
                    onChange={(value) =>
                      updateNode(node.id, {
                        nodeType: Number(value) || NODE_TYPE.TASK,
                        approverType: null,
                        approverUserId: null,
                        approverUserIds: [],
                        approverRoleId: null,
                        approverGroupId: null,
                      })
                    }
                  />
                  <SelectField
                    Dropdown={Dropdown}
                    label={t('editor_node_field_connect')}
                    options={connectOptions}
                    value=''
                    placeholder={t('editor_node_connect_placeholder')}
                    onChange={(target) => {
                      if (!target) return;
                      addLink(node.id, target);
                    }}
                  />

                  {!isApproval && (() => {
                    const taskOptions = getTaskNomenclatureOptions(node.data.code);
                    const selectedTaskOption =
                      findOption(taskOptions, node.data.code) || null;

                    return ComboBox ? (
                      <ComboBox
                        label={t('editor_node_field_task')}
                        placeholder={t('editor_node_task_placeholder')}
                        options={taskOptions}
                        value={selectedTaskOption}
                        onChange={(selected) => {
                          const nextValue = String(
                            typeof selected === 'string'
                              ? selected
                              : selected?.value || ''
                          ).trim();

                          const patch = { code: nextValue };
                          const currentName = String(node.data.name || '').trim();
                          if (!currentName || /^tarea(\s|$)/i.test(currentName)) {
                            patch.name = nextValue
                              ? `${t('editor_node_type_task')} ${nextValue}`
                              : t('editor_node_type_task');
                          }

                          updateNode(node.id, patch);
                        }}
                      />
                    ) : (
                      <SelectField
                        Dropdown={Dropdown}
                        label={t('editor_node_field_task')}
                        placeholder={t('editor_node_task_placeholder')}
                        options={taskOptions}
                        value={node.data.code || ''}
                        onChange={(value) => {
                          const nextValue = String(value || '').trim();
                          const patch = { code: nextValue };
                          const currentName = String(node.data.name || '').trim();
                          if (!currentName || /^tarea(\s|$)/i.test(currentName)) {
                            patch.name = nextValue
                              ? `${t('editor_node_type_task')} ${nextValue}`
                              : t('editor_node_type_task');
                          }
                          updateNode(node.id, patch);
                        }}
                      />
                    );
                  })()}

                  {isApproval && (
                    <>
                      <SelectField
                        Dropdown={Dropdown}
                        label={t('editor_node_approver_type')}
                        options={approverTypeOptions}
                        value={approverType}
                        onChange={(value) =>
                          updateNode(node.id, {
                            approverType: Number(value) || null,
                            approverUserId: null,
                            approverUserIds: [],
                            approverRoleId: null,
                            approverGroupId: null,
                          })
                        }
                      />
                      {approverType === APPROVER_TYPE.USER ? (
                        approverOptions.length > 0 ? (
                          <ComboBox
                            label={t('editor_node_approvers')}
                            placeholder={t('editor_node_approvers_placeholder')}
                            options={approverOptions}
                            value={selectedApproverUserOptions}
                            onChange={(selectedItems) => {
                              const values = normalizeNumberArray(
                                Array.isArray(selectedItems)
                                  ? selectedItems.map((item) => item?.value)
                                  : []
                              );
                              updateNode(node.id, {
                                approverUserIds: values,
                                approverUserId:
                                  values.length === 1 ? values[0] : null,
                              });
                            }}
                            multiSelect
                            disabled={!approverType}
                          />
                        ) : (
                          <Input
                            label={t('editor_node_approver_ids')}
                            placeholder={t('editor_node_approver_ids_placeholder')}
                            disabled={!approverType}
                            value={selectedApproverUserIds.join(',')}
                            onChange={(event) => {
                              const normalizedValues = normalizeNumberArray(
                                String(event.target.value || '')
                                  .split(',')
                                  .map((item) => item.trim())
                                  .filter(Boolean)
                              );
                              updateNode(node.id, {
                                approverUserIds: normalizedValues,
                                approverUserId:
                                  normalizedValues.length === 1
                                    ? normalizedValues[0]
                                    : null,
                              });
                            }}
                          />
                        )
                      ) : approverOptions.length > 0 ? (
                        <SelectField
                          Dropdown={Dropdown}
                          label={t('editor_node_approver')}
                          options={approverOptions}
                          value={selectedApproverValue}
                          placeholder={t('editor_node_approver_placeholder')}
                          onChange={(value) =>
                            updateNode(node.id, {
                              approverUserId:
                                approverType === APPROVER_TYPE.USER
                                  ? Number(value) || null
                                  : null,
                              approverRoleId:
                                approverType === APPROVER_TYPE.ROLE
                                  ? Number(value) || null
                                  : null,
                              approverGroupId:
                                approverType === APPROVER_TYPE.GROUP
                                  ? Number(value) || null
                                  : null,
                            })
                          }
                          disabled={!approverType}
                        />
                      ) : (
                        <Input
                          label={t('editor_node_approver_id')}
                          type='number'
                          placeholder={t('editor_node_approver_id_placeholder')}
                          disabled={!approverType}
                          value={selectedApproverValue || ''}
                          onChange={(event) => {
                            const value = Number(event.target.value) || null;
                            updateNode(node.id, {
                              approverUserId:
                                approverType === APPROVER_TYPE.USER ? value : null,
                              approverRoleId:
                                approverType === APPROVER_TYPE.ROLE ? value : null,
                              approverGroupId:
                                approverType === APPROVER_TYPE.GROUP ? value : null,
                            });
                          }}
                        />
                      )}
                    </>
                  )}
                </div>

                <div className='workflow-editor-step__links'>
                  <p className='workflow-editor-step__links-title'>{t('editor_node_connections_title')}</p>
                  {outgoingLinks.length === 0 ? (
                    <p className='workflow-editor-step__links-empty'>{t('editor_node_no_connections')}</p>
                  ) : (
                    <div className='workflow-editor-step__links-list'>
                      {outgoingLinks.map((link) => (
                        <div key={link.id} className='workflow-editor-step__link-item'>
                          <span className='workflow-editor-step__link-label'>
                            {link.target === END_NODE_ID
                              ? t('editor_node_finalization')
                              : workflowLabelById[link.target] || link.target}
                          </span>
                          <button
                            type='button'
                            className='workflow-editor-step__link-remove'
                            onClick={() => removeLink(node.id, link.target)}
                          >
                            {t('editor_node_btn_remove_link')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className='workflow-editor-step__actions'>
                  <button
                    type='button'
                    className='workflow-editor-step__focus-btn'
                    onClick={() => setSelectedNodeId(node.id)}
                  >
                    {t('editor_node_btn_select')}
                  </button>
                  {IconButton ? (
                    <IconButton
                      ariaLabel={t('editor_node_aria_delete')}
                      icon='trash'
                      size='md'
                      onClick={() => removeWorkflowNode(node.id)}
                    />
                  ) : (
                    <button
                      type='button'
                      className='workflow-editor-step__delete-btn'
                      onClick={() => removeWorkflowNode(node.id)}
                    >
                      {t('editor_node_btn_delete')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default WorkflowEditorModal;
